import React, { useMemo } from 'react';

import * as style from './pack-card.css.ts';
import { InfoIcon } from '../../assets/icons/info';
import { formatNumber, truncateItemName } from '../../utils';
import { AvailableLabel } from '../AvailableLabel';
import { CardButtons } from '../CardButtons';
import { RewardBadges } from '../RewardBadges';
import { useStoreCard } from '../StoreCardWrapper/store-card-context.ts';

import type { Bundle, Item } from '@site-builder/store-sdk';

const getItemDisplay = (
  item: Item,
  quantity: number,
): { name: string; quantity: number } => {
  const asBundle = item as Bundle;
  if (asBundle.content?.length) {
    const inner = asBundle.content[0];
    return { name: inner.originalItem.name, quantity: inner.quantity };
  }
  return { name: item.name, quantity };
};

const WEB_EXCLUSIVE_URL =
  'https://cdn.xsolla.net/image-proxy/rs:fit:1920:0/f:webp/storage/merchant-bucket-prod/files/uploaded/sitebuilder/439172/029837f394f48a8e56df220d43229140.png@webp';
const WEB_ONLY_URL =
  'https://cdn.xsolla.net/image-proxy/rs:fit:1920:0/f:webp/storage/merchant-bucket-prod/files/uploaded/sitebuilder/439172/409ab8ce5017df24273ddbfc05e35b35.png@webp';

interface PackCardProps {
  readonly data: Bundle;
  readonly onCartClick?: () => void;
}

interface LoyaltyReward {
  amount: number;
  imageUrl: string | null;
}

interface BundleWithLoyaltyRewards extends Bundle {
  loyaltyRewards?: LoyaltyReward[];
}

export const PackCard = React.memo(({ data, onCartClick }: PackCardProps) => {
  const bundle = data as BundleWithLoyaltyRewards;

  const vpRewardItems = useMemo(
    () =>
      bundle.vpRewards?.map(({ amount, imageUrl }) => ({ amount, imageUrl })) ??
      [],
    [bundle],
  );

  const loyaltyRewardItems = useMemo(
    () =>
      bundle.loyaltyRewards?.map(({ amount, imageUrl }) => ({
        amount,
        imageUrl,
      })) ?? [],
    [bundle],
  );

  const { hasLimit, availableAmount, maxAvailableAmount } = useStoreCard();

  const webBadgeType = useMemo(() => {
    if (!bundle.attributes?.length) return null;
    if (bundle.attributes.some((attr) => attr.externalId === 'web_exclusive'))
      return 'web_exclusive';
    if (bundle.attributes.some((attr) => attr.externalId === 'web_only'))
      return 'web_only';
    return null;
  }, [bundle]);

  const webBonusPercent = useMemo(() => {
    if (!bundle.attributes?.length) return null;
    const bonusAttr = bundle.attributes.find((attr) =>
      attr.externalId.startsWith('value_bonus_'),
    );
    if (!bonusAttr) return null;
    const valueEntry = bonusAttr.values[0];
    return (
      valueEntry?.value ?? bonusAttr.externalId.replace('value_bonus_', '')
    );
  }, [bundle]);

  const itemsCount = useMemo(() => {
    if (bundle.type === 'bundle') {
      return bundle.content.length;
    }
    return 1;
  }, [bundle]);

  const getContentItemClassName = () => {
    if (itemsCount === 5) {
      return `${style.contentItem} ${style.contentItem5Items}`;
    }
    return style.contentItem;
  };

  const renderContent = () => {
    if (bundle.type === 'bundle') {
      const items = bundle.content;

      return (
        <>
          {items.map(
            (
              {
                originalItem: item,
                quantity,
              }: {
                originalItem: Item;
                quantity: number;
              },
              index,
            ) => {
              const { name: displayName, quantity: displayQuantity } =
                getItemDisplay(item, quantity);
              return (
                <div key={item.sku} className={getContentItemClassName()}>
                  <img
                    className={style.contentItemImage}
                    src={item.imageUrl ?? ''}
                    alt={displayName}
                  />
                  <div className={style.contentItemLabel}>
                    <span className={style.contentItemText}>
                      {formatNumber(displayQuantity)}
                    </span>
                    <span className={style.contentItemText}>
                      {truncateItemName(displayName)}
                    </span>
                  </div>
                  {index === items.length - 1 && webBadgeType && (
                    <div
                      className={
                        webBadgeType === 'web_exclusive'
                          ? style.exclusiveBadge
                          : style.webOnlyBadge
                      }
                    >
                      <img
                        src={
                          webBadgeType === 'web_exclusive'
                            ? WEB_EXCLUSIVE_URL
                            : WEB_ONLY_URL
                        }
                        alt={
                          webBadgeType === 'web_exclusive'
                            ? 'Web Exclusive'
                            : 'Web Only'
                        }
                      />
                    </div>
                  )}
                </div>
              );
            },
          )}
        </>
      );
    }

    return (
      <div className={style.contentItem}>
        <img
          className={style.contentItemImage}
          src={data.imageUrl ?? ''}
          alt={data.name}
        />
        <div className={style.contentItemLabel}>
          <span className={style.contentItemText}>1</span>
          <span className={style.contentItemText}>
            {truncateItemName(data.name)}
          </span>
        </div>
        {webBadgeType && (
          <div
            className={
              webBadgeType === 'web_exclusive'
                ? style.exclusiveBadge
                : style.webOnlyBadge
            }
          >
            <img
              src={
                webBadgeType === 'web_exclusive'
                  ? WEB_EXCLUSIVE_URL
                  : WEB_ONLY_URL
              }
              alt={
                webBadgeType === 'web_exclusive' ? 'Web Exclusive' : 'Web Only'
              }
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={style.block}>
      <button
        className={style.infoButton}
        type='button'
        aria-label='Info'
        onClick={onCartClick}
      >
        <InfoIcon size={24} />
      </button>
      <div className={`${style.blockInner} msm-frame msm-frame--brown`}>
        <div className='msm-frame__corners'></div>
        <div className={style.about}>
          <h2
            className={`${style.title} msm-frame__title msm-frame__title--wooden`}
          >
            {data.name}
          </h2>
        </div>

        <div className={style.contentWrapper}>
          <div className={style.content}>{renderContent()}</div>
          <CardButtons />
          <div className={style.bottomWrapper}>
            <RewardBadges
              rewardPoints={vpRewardItems}
              loyaltyPoints={loyaltyRewardItems}
            />
            {hasLimit && (
              <AvailableLabel
                available={availableAmount}
                total={maxAvailableAmount}
              />
            )}
          </div>
        </div>
        <div className={`${style.msmBadgeWrapper} msm-badge-wrapper`}>
          {webBonusPercent && (
            <div className='msm-badge msm-badge--green'>
              <p className='msm-badge__value'>+{webBonusPercent}%</p>
              <p className='msm-badge__label'>Value</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

PackCard.displayName = 'PackCard';
